import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Mail, Phone, MapPin, Building2, ExternalLink
} from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import PublicHeader from '@/components/PublicHeader';
import { useState, useEffect } from 'react';
import { API } from '@/App';

export default function ContactPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setUser(await response.json());
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {user && <Sidebar user={user} />}
      {!user && <PublicHeader breadcrumbs={[{ label: 'Contact Us' }]} />}
      
      <div className={`flex-1 flex flex-col ${user ? 'lg:ml-64' : ''}`}>
        <div className="flex-1 overflow-y-auto">
          <div className={`max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 ${!user ? 'pt-24 sm:pt-32' : 'lg:pt-8 pt-20'}`}>
            
            {/* Header */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Contact Us</h1>
              <p className="text-base sm:text-lg text-slate-500 mt-2">Get in touch with our team</p>
            </div>

            {/* Product Attribution */}
            <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-violet-50 border border-violet-200 rounded-xl">
              <p className="text-xs sm:text-sm text-violet-800 text-center">
                <Building2 className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
                <strong>TwentyDocs</strong> is a Twenty Digital Product
              </p>
            </div>

            {/* Contact Information Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Company Info */}
              <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                    <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900">Company</h2>
                    <p className="text-xs sm:text-sm text-slate-500">Our official business information</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-slate-700">Twenty Digital, Inc</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-blue-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900">Address</h2>
                    <p className="text-xs sm:text-sm text-slate-500">Visit us or send mail</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs sm:text-sm text-slate-700">131 Continental Dr, Suite 305</p>
                  <p className="text-xs sm:text-sm text-slate-700">Newark, DE 19713</p>
                  <p className="text-xs sm:text-sm text-slate-700">United States</p>
                </div>
              </div>

              {/* Email */}
              <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-green-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900">Email</h2>
                    <p className="text-xs sm:text-sm text-slate-500">Send us a message</p>
                  </div>
                </div>
                <a 
                  href="mailto:projects@twenty-digital.com"
                  className="text-violet-600 hover:text-violet-700 font-medium flex items-center gap-2 text-xs sm:text-sm break-all"
                >
                  projects@twenty-digital.com
                  <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                </a>
              </div>

              {/* Phone */}
              <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-orange-100 flex items-center justify-center">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900">Phone</h2>
                    <p className="text-xs sm:text-sm text-slate-500">Call us directly</p>
                  </div>
                </div>
                <a 
                  href="tel:+13022814587"
                  className="text-violet-600 hover:text-violet-700 font-medium flex items-center gap-2 text-sm sm:text-base"
                >
                  302-281-4587
                  <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                </a>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 p-6 sm:p-8 text-center">
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3">Need Help?</h3>
              <p className="text-sm sm:text-base text-slate-600 mb-6">
                Our team is here to assist you with any questions about TwentyDocs, 
                technical support, or custom solutions for your practice.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                <Button
                  onClick={() => window.location.href = 'mailto:projects@twenty-digital.com'}
                  className="bg-violet-600 hover:bg-violet-700 w-full sm:w-auto text-sm sm:text-base"
                >
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Email Us
                </Button>
                <Button
                  onClick={() => window.location.href = 'tel:+13022814587'}
                  variant="outline"
                  className="w-full sm:w-auto text-sm sm:text-base"
                >
                  <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  Call Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
